import React from 'react';

import { Button, Grid } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { STATUSES_OPTIONS } from '@clearverify/constants';
import { toClearVerifyStaffRole } from '@clearverify/utils';

import {
  PlatformStaffItemFragment,
  PlatformStaffListDocument,
  UserStatus,
  useUpdateClearVerifyStaffMutation,
} from 'src/api';
import { SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { ClearVerifyStaffAccessFormPart } from '../../../ClearVerifyStaffAccessFormPart';

import { FormFields } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

interface PlatformStaffDetailModalContentFormProps {
  platformStaff: PlatformStaffItemFragment;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const PlatformStaffDetailModalContentForm: React.FC<
  PlatformStaffDetailModalContentFormProps
> = ({ platformStaff, closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  // A pending invite keeps its status: the select stays hidden and the current value is sent back.
  const isPending = platformStaff.user.status === UserStatus.Pending;

  const [updateClearVerifyStaff] = useUpdateClearVerifyStaffMutation({
    refetchQueries: [PlatformStaffListDocument],
    context: createApolloContext({
      successMessage: 'Clear Verify Staff updated successfully.',
      errorMessage: 'Failed to update Clear Verify Staff',
    }),
  });

  const handleClose = React.useCallback(() => closeModal(), [closeModal]);

  const handleSubmit = React.useCallback(
    async ({ status, ...access }: FormValues) => {
      switchClosability(false);

      try {
        await updateClearVerifyStaff({
          variables: { platformStaffId: platformStaff.id, access, userStatus: status },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [switchClosability, closeModal, platformStaff.id, updateClearVerifyStaff],
  );

  const initialValues: FormValues = React.useMemo(
    () => ({
      [FormFields.status]: platformStaff.user.status,
      [FormFields.role]: toClearVerifyStaffRole(platformStaff),
      [FormFields.isFinancialReportAvailable]: platformStaff.isFinancialReportAvailable,
      [FormFields.isRecipientForMonthlyFinancialReport]:
        platformStaff.isRecipientForMonthlyFinancialReport,
    }),
    [platformStaff],
  );

  return (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={formValidationSchema}
      subscription={{ valid: true, submitting: true, dirty: true }}
      containerProps={{ mt: 2 }}
    >
      {({ submitting, valid, dirty }) => {
        return (
          <React.Fragment>
            {!isPending && (
              <Grid size={12}>
                <Field
                  label="Status"
                  name={FormFields.status}
                  component={SelectField}
                  options={STATUSES_OPTIONS}
                  disabled={submitting}
                  fullWidth
                />
              </Grid>
            )}

            <Grid size={12} mt={2}>
              <ClearVerifyStaffAccessFormPart disabled={submitting} />
            </Grid>

            <Grid
              size={12}
              display="flex"
              alignContent="center"
              justifyContent="center"
              gap={2}
              mt={4}
            >
              <Button variant="outlined" color="error" onClick={handleClose} disabled={submitting}>
                Close
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting || !dirty}
                loading={submitting}
              >
                Save Changes
              </Button>
            </Grid>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
